/**
 * Analytics for parents (admin console spec, AC5; DPDP Act s.6 and s.9(3); privacy policy "Cookies"):
 *
 * 1. First-party, always on: anonymous page counts and home assessment progress sent to our own API. No
 *    identifier, no cookie; routes are reduced to templates so ids never leave the page.
 * 2. Google Analytics 4 (VITE_GA_ID) and/or PostHog EU (VITE_POSTHOG_KEY) — either, both or
 *    neither, as the team chooses — only after the parent opts in, and only on the public pages the
 *    privacy policy lists. GA is switched off (`ga-disable-<id>`) the moment the visitor moves to
 *    any other page; PostHog sends only what we capture (autocapture, replay, heatmaps and remote
 *    config off), and we capture nothing off the public pages.
 *
 * Nothing about a child — name, age, answers, results — is ever sent to either.
 */
import { API_URL, api } from '../api/client';

const env = (import.meta.env ?? {}) as Record<string, string | undefined>;
export const GA_ID = env.VITE_GA_ID ?? '';
export const POSTHOG_KEY = env.VITE_POSTHOG_KEY ?? '';
const POSTHOG_HOST = env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com';

export const optionalAnalyticsConfigured = () => Boolean(GA_ID || POSTHOG_KEY);

const store = {
  get: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k: string, v: string) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* private mode */
    }
  },
};

// ── Route templates ────────────────────────────────────────────────────────

/** uuids, report refs, numbers, and long tokens (mixed case or digits); readable slugs are kept. */
const isIdLike = (seg: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(seg) ||
  /^NUR-/i.test(seg) ||
  /^\d+$/.test(seg) ||
  (seg.length >= 16 && /[0-9A-Z_]/.test(seg) && !/^[a-z]+(-[a-z0-9]+)*$/.test(seg));

/** `/reports/3f2a…/programme` → `/reports/:id/programme`; tokens and refs never leave the page. */
export const pageTemplate = (pathname: string): string =>
  pathname
    .split('/')
    .map((seg) => (isIdLike(seg) ? ':id' : seg))
    .join('/')
    .replace(/\/(shared|join)\/[^/]+/, '/$1/:token') || '/';

/** The public pages Google Analytics may run on — the same list as the privacy policy. */
const MARKETING = [/^\/$/, /^\/faq$/, /^\/learn(\/|$)/, /^\/trust$/, /^\/privacy$/, /^\/terms$/, /^\/refunds$/, /^\/signin$/, /^\/signup$/, /^\/data-request$/];

export const isMarketingPage = (path: string) => MARKETING.some((r) => r.test(path));

// ── Layer 1: first-party, anonymous ────────────────────────────────────────

let clientCountsOn = true;
export const setClientCounts = (on: boolean) => {
  clientCountsOn = on;
};

export const track = (
  name: 'page_view' | 'checkin_page' | 'cta_click' | 'consent' | 'engaged' | 'campaign_landing',
  props: Record<string, string | number> = {},
  opts: { withSession?: boolean } = {},
) => {
  if (!clientCountsOn || typeof fetch === 'undefined') return;
  // Automated browsers (tests, crawlers that run scripts) are not visitors; the API filters by user agent too.
  if (typeof navigator !== 'undefined' && navigator.webdriver) return;
  try {
    // A page view may carry the parent's session so the API can count signed-in users once a day
    // (it stores a daily-changing code, never the id). Nothing else ever does.
    const token = opts.withSession ? api.token.get() : null;
    void fetch(`${API_URL}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ name, props }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* analytics never breaks the page */
  }
};

/**
 * A tracked call to action: a short fixed id (`hero_start`, `nav_start`, …), counted anonymously.
 * Never a URL, a label in the parent's language, or anything typed.
 */
export const trackCta = (id: string) => track('cta_click', { id });

// ── First-touch attribution (sent once, with the sign-up) ──────────────────

const ATTRIBUTION_KEY = 'nurture.attribution';

export interface Attribution {
  source?: string;
  medium?: string;
  campaign?: string;
  referrer?: string;
  landing?: string;
  code?: string;
}

const clip = (v: string | null | undefined, n = 60) => (v ? v.trim().slice(0, n) : undefined);

/** Keeps the first campaign that brought this browser here (never overwritten by later visits). */
export const captureAttribution = (url: URL, documentReferrer: string) => {
  if (store.get(ATTRIBUTION_KEY)) return;
  const p = url.searchParams;
  let refHost: string | undefined;
  try {
    refHost = documentReferrer ? new URL(documentReferrer).hostname : undefined;
  } catch {
    refHost = undefined;
  }
  const external = refHost && refHost !== url.hostname ? refHost : undefined;
  const a: Attribution = {
    source: clip(p.get('utm_source')) ?? external,
    medium: clip(p.get('utm_medium')) ?? (external ? 'referral' : undefined),
    campaign: clip(p.get('utm_campaign')),
    referrer: clip(external, 120),
    landing: clip(pageTemplate(url.pathname), 120),
    code: clip(p.get('code') ?? p.get('ref'), 32)?.toUpperCase(),
  };
  if (a.source || a.campaign || a.code) store.set(ATTRIBUTION_KEY, JSON.stringify(a));
};

/**
 * A visit that arrived on a tagged link (a tap on a shared link or a QR scan), counted once per
 * page load; also keeps the first-touch campaign. Values only, never the full address.
 */
let landed = '';
export const noteCampaign = (url: URL, documentReferrer: string) => {
  captureAttribution(url, documentReferrer);
  const p = url.searchParams;
  if (!p.get('utm_source') && !p.get('utm_campaign')) return;
  const key = url.search;
  if (landed === key) return;
  landed = key;
  track('campaign_landing', {
    ...(clip(p.get('utm_source')) ? { source: clip(p.get('utm_source'))! } : {}),
    ...(clip(p.get('utm_medium')) ? { medium: clip(p.get('utm_medium'))! } : {}),
    ...(clip(p.get('utm_campaign')) ? { campaign: clip(p.get('utm_campaign'))! } : {}),
    ...(clip(p.get('utm_content')) ? { content: clip(p.get('utm_content'))! } : {}),
  });
};

export const getAttribution = (): Attribution | undefined => {
  const raw = store.get(ATTRIBUTION_KEY);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as Attribution;
  } catch {
    return undefined;
  }
};

// ── Consent for layers 2 and 3 ─────────────────────────────────────────────

const CONSENT_KEY = 'nurture.consent.analytics';
export type ConsentChoice = 'granted' | 'denied';

export const getConsent = (): ConsentChoice | null => {
  const v = store.get(CONSENT_KEY);
  return v === 'granted' || v === 'denied' ? v : null;
};

type Listener = (c: ConsentChoice | null) => void;
const listeners = new Set<Listener>();
export const onConsentChange = (fn: Listener) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
let reopen: (() => void) | undefined;
export const setConsentReopener = (fn: (() => void) | undefined) => {
  reopen = fn;
};
export const openConsentSettings = () => reopen?.();

export const setConsent = (choice: ConsentChoice) => {
  store.set(CONSENT_KEY, choice);
  track('consent', { choice });
  if (choice === 'denied') optOutThirdParty();
  listeners.forEach((fn) => fn(choice));
};

// ── Layers 2 and 3 loaders ─────────────────────────────────────────────────

type Gtag = (...args: unknown[]) => void;
type PostHog = { init: (key: string, opts: Record<string, unknown>) => void; capture: (e: string, p?: Record<string, unknown>) => void; opt_out_capturing: () => void; opt_in_capturing: () => void };
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
    posthog?: PostHog & unknown[];
  }
}

const addScript = (src: string) => {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  s.crossOrigin = 'anonymous';
  document.head.appendChild(s);
};

let gaLoaded = false;
const loadGa = () => {
  if (gaLoaded || !GA_ID) return;
  gaLoaded = true;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  addScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`);
};

let phLoaded = false;
const loadPostHog = () => {
  if (phLoaded || !POSTHOG_KEY) return;
  phLoaded = true;
  // The official loader's stub: calls queue until the library arrives.
  const stub = [] as unknown as PostHog & unknown[] & { _i: unknown[] };
  stub._i = [];
  for (const m of ['capture', 'opt_out_capturing', 'opt_in_capturing']) {
    (stub as unknown as Record<string, unknown>)[m] = (...args: unknown[]) => stub.push([m, ...args]);
  }
  stub.init = (key: string, opts: Record<string, unknown>) => {
    stub._i.push([key, opts, 'posthog']);
  };
  window.posthog = stub;
  addScript(`${POSTHOG_HOST.replace('.i.posthog.com', '-assets.i.posthog.com')}/static/array.js`);
  stub.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Remote config off: nothing set in the PostHog dashboard can switch on replay or autocapture here.
    advanced_disable_decide: true,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    capture_heatmaps: false,
    capture_dead_clicks: false,
    capture_performance: false,
    disable_session_recording: true,
    disable_surveys: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    persistence: 'localStorage',
    person_profiles: 'identified_only',
    ip: false,
  });
};

/** Google's documented off switch: while true, gtag.js sends nothing. */
const setGaDisabled = (disabled: boolean) => {
  if (GA_ID) (window as unknown as Record<string, boolean>)[`ga-disable-${GA_ID}`] = disabled;
};
const optOutThirdParty = () => {
  setGaDisabled(true);
  window.posthog?.opt_out_capturing?.();
};

// ── Time on page (first-party, anonymous) ──────────────────────────────────

let current: { page: string; visibleSince: number | null; ms: number } | null = null;

const pause = () => {
  if (current?.visibleSince != null) {
    current.ms += Date.now() - current.visibleSince;
    current.visibleSince = null;
  }
};
const resume = () => {
  if (current && current.visibleSince == null) current.visibleSince = Date.now();
};
/** Sends the seconds the last page was visible (whole seconds only; the API caps at 30 minutes). */
export const flushEngaged = () => {
  if (!current) return;
  pause();
  const s = Math.round(current.ms / 1000);
  if (s >= 1) track('engaged', { page: current.page, s });
  current = null;
};

let listening = false;
const listen = () => {
  if (listening || typeof document === 'undefined') return;
  listening = true;
  document.addEventListener('visibilitychange', () => (document.visibilityState === 'hidden' ? pause() : resume()));
  window.addEventListener('pagehide', flushEngaged);
};

/** Page view: first-party always; Google/PostHog only with consent, and only on the public pages. */
export const trackPage = (pathname: string) => {
  flushEngaged();
  if (pathname.startsWith('/admin')) {
    setGaDisabled(true);
    return;
  }
  const page = pageTemplate(pathname);
  const signedIn = Boolean(api.token.get());
  if (typeof window !== 'undefined') noteCampaign(new URL(window.location.href), document.referrer);
  // The first tagged link that brought this browser, so visitors can be counted per campaign.
  const first = getAttribution();
  const campaign = { ...(first?.campaign ? { campaign: first.campaign } : {}), ...(first?.source ? { source: first.source } : {}) };
  track('page_view', { page, auth: signedIn ? 'in' : 'out', ...campaign }, { withSession: signedIn });
  listen();
  current = { page, visibleSince: typeof document === 'undefined' || document.visibilityState !== 'hidden' ? Date.now() : null, ms: 0 };
  const gpc = typeof navigator !== 'undefined' && Boolean((navigator as { globalPrivacyControl?: boolean }).globalPrivacyControl);
  const allowed = getConsent() === 'granted' && !gpc && isMarketingPage(pathname);
  // Switch GA off before anything else can fire on a non-public page (it stays loaded in the SPA).
  setGaDisabled(!allowed);
  if (!allowed) return;
  if (GA_ID) {
    loadGa();
    window.gtag?.('event', 'page_view', { page_path: page, page_location: `${window.location.origin}${page}`, page_title: page });
  }
  if (POSTHOG_KEY) {
    loadPostHog();
    window.posthog?.opt_in_capturing?.();
    window.posthog?.capture('$pageview', { $current_url: `${window.location.origin}${page}`, $pathname: page });
  }
};
